import {
  creeUnCycle,
  projetModification,
  promptProjetClaude,
  STATUTS_PROJET,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { lazy, Suspense, useState } from 'react'
import { api, corps, ecrit, type FicheProjet, lire, requeteMoi, requetes } from '../api'
import { BoutonClaude } from '../composants/BoutonClaude'
import { ClaudeProjet } from '../composants/ClaudeProjet'
import { confirmer } from '../composants/confirmation'
import { couleurPersonne, useDirectProjet } from '../composants/direct'
import { EnLigne } from '../composants/EnLigne'
import { EnvoisProjet } from '../composants/EnvoisProjet'
import { FilsProjet } from '../composants/FilsProjet'
import { Formulaire } from '../composants/Formulaire'
import { Icone } from '../composants/Icone'
import { Vide } from '../composants/Section'
import { TachesProjet } from '../composants/TachesProjet'
import { Onglets } from '../composants/Ui'
import { initiales, LIBELLES_STATUT_PROJET } from '../libelles'
import { periodeProjet } from './Projets'

// L'éditeur (Tiptap, Yjs) n'est chargé qu'à l'ouverture d'un projet.
const EditeurPage = lazy(() =>
  import('../composants/EditeurPage').then((m) => ({ default: m.EditeurPage })),
)

// Un projet en onglets : ses pages (l'objectif et ses sous-pages, éditées à plusieurs en même
// temps), ses tâches, ses fils de commentaires, et le lien vers Claude. Tout se met à jour seul
// quand quelqu'un d'autre change le projet. L'onglet et la page ouverts sont dans l'adresse.

export const ONGLETS_PROJET = ['pages', 'taches', 'commentaires', 'envois', 'claude'] as const
export type OngletProjet = (typeof ONGLETS_PROJET)[number]

export function ProjetFiche({
  id,
  pageId,
  onglet,
  onNaviguer,
}: {
  id: string
  pageId: string | undefined
  onglet: OngletProjet
  onNaviguer: (vers: { onglet?: OngletProjet; page?: string | undefined }) => void
}) {
  const projet = useQuery(requetes.projet(id))
  const moi = useQuery(requeteMoi).data
  const client = useQueryClient()
  const { presents, connecte } = useDirectProjet(id)
  // Envois des tâches du projet, à traiter : le compte de l'onglet.
  const aTraiter = useQuery(requetes.brouillons({ projetId: id, vue: 'a_traiter' })).data?.length
  // Relire la fiche après un geste (le signal en direct le fait aussi, un peu après).
  const relire = () => client.invalidateQueries({ queryKey: ['projets', 'fiche', id] })
  const [filActif, setFilActif] = useState<string | null>(null)
  // Passage à montrer en ouvrant sa page (depuis un fil de l'onglet Commentaires).
  const [passage, setPassage] = useState<string | null>(null)
  if (projet.isPending)
    return (
      <main>
        <Vide>Chargement…</Vide>
      </main>
    )
  if (projet.isError || !moi)
    return (
      <main>
        <Vide>Projet introuvable.</Vide>
      </main>
    )
  const p = projet.data
  const racine = p.pages.find((x) => x.parentId === null)!
  const page = p.pages.find((x) => x.id === pageId) ?? racine
  const ouvrirPage = (x: string) =>
    onNaviguer({ onglet: 'pages', page: x === racine.id ? undefined : x })
  const lienProjet = `${window.location.origin}/projets/${p.id}`
  return (
    <main className="fiche-projet">
      <EnteteProjet
        p={p}
        presents={presents}
        connecte={connecte}
        prompt={promptProjetClaude(p, lienProjet)}
      />
      <Onglets
        valeur={onglet}
        onChange={(o) => onNaviguer({ onglet: o, page: pageId })}
        defilants
        onglets={[
          { valeur: 'pages', libelle: 'Pages', icone: 'description', compte: p.pages.length },
          {
            valeur: 'taches',
            libelle: 'Tâches',
            icone: 'task_alt',
            compte: p.avancement.ouvertes,
          },
          {
            valeur: 'commentaires',
            libelle: 'Commentaires',
            icone: 'forum',
            compte: p.fils.filter((f) => !f.resoluLe).length,
          },
          { valeur: 'envois', libelle: 'Envois', icone: 'outbox', compte: aTraiter },
          { valeur: 'claude', libelle: 'Claude', icone: 'auto_awesome' },
        ]}
      />
      {onglet === 'pages' && (
        <section className="carte projet-page">
          <OngletsPages p={p} page={page} onOuvrir={ouvrirPage} />
          <TitrePage p={p} page={page} onOuvrir={ouvrirPage} />
          <Suspense fallback={<Vide>Chargement de l’éditeur…</Vide>}>
            <EditeurPage
              key={page.id}
              pageId={page.id}
              ecriture={p.ecriture}
              moi={{ id: moi.id, nom: moi.nom }}
              fils={p.fils.filter((f) => f.pageId === page.id && f.extrait && !f.resoluLe)}
              passage={passage}
              onPassageMontre={() => setPassage(null)}
              onCommenter={async (extrait, texte) => {
                const fil = await lire(
                  api.projets[':id'].fils.$post({
                    param: { id: p.id },
                    json: { texte, pageId: page.id, extrait },
                  }),
                )
                await relire()
                return fil.id
              }}
              onRepondre={async (filId, texte) => {
                await lire(
                  api.projets.fils[':id'].reponses.$post({ param: { id: filId }, json: { texte } }),
                )
                await relire()
              }}
              onResoudre={async (filId) => {
                await lire(
                  api.projets.fils[':id'].resolution.$post({
                    param: { id: filId },
                    json: { resolu: true },
                  }),
                )
                await relire()
              }}
              onVoirFil={(fil) => {
                setFilActif(fil)
                onNaviguer({ onglet: 'commentaires', page: pageId })
              }}
            />
          </Suspense>
        </section>
      )}
      {onglet === 'taches' && (
        <section className="carte">
          <TachesProjet projet={p} />
        </section>
      )}
      {onglet === 'commentaires' && (
        <section className="carte">
          <FilsProjet
            projet={p}
            pageId={page.id}
            filActif={filActif}
            onVoirPassage={(fil) => {
              const f = p.fils.find((x) => x.id === fil)
              setFilActif(fil)
              setPassage(fil)
              if (f?.pageId) ouvrirPage(f.pageId)
            }}
          />
        </section>
      )}
      {onglet === 'envois' && (
        <section className="carte">
          <EnvoisProjet projet={p} />
        </section>
      )}
      {onglet === 'claude' && (
        <section className="carte">
          <ClaudeProjet p={p} page={page.id === racine.id ? null : page} lien={lienProjet} />
        </section>
      )}
    </main>
  )
}

function EnteteProjet({
  p,
  presents,
  connecte,
  prompt,
}: {
  p: FicheProjet
  presents: { id: string; nom: string }[]
  connecte: boolean
  /** Le prompt du lien vers Claude, sur tout le projet. */
  prompt: string
}) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const entites = useQuery(requetes.entites).data ?? []
  const [erreur, setErreur] = useState<string | null>(null)
  const modifier = async (v: Record<string, unknown>) => {
    setErreur(null)
    try {
      await lire(
        api.projets[':id'].$patch({ param: { id: p.id }, json: corps(projetModification, v) }),
      )
      await client.invalidateQueries({ queryKey: ['projets'] })
    } catch (e) {
      setErreur((e as Error).message)
      throw e
    }
  }
  const periode = periodeProjet(p)
  const { faites, total } = p.avancement
  return (
    <header className="entete-projet">
      <Link to="/projets" className="lien-fleche retour">
        <Icone nom="arrow_back" />
        Projets
      </Link>
      <div className="entete-projet-ligne">
        <div className="entete-page-titre">
          <span className="surtitre">{p.entite ?? 'Sans entité'}</span>
          <h1>
            <EnLigne
              libelle="le titre"
              champs={[{ nom: 'titre', libelle: 'Titre', type: 'texte', requis: true }]}
              initial={p}
              modifiable={p.ecriture}
              onEnregistrer={modifier}
            >
              {p.titre}
            </EnLigne>
          </h1>
        </div>
        <div className="presence" aria-label="Sur ce projet en ce moment">
          <BoutonClaude prompt={prompt} taille="icone" />
          <span
            className={connecte ? 'pastille en-ligne' : 'pastille'}
            title={connecte ? 'En direct' : 'Reconnexion…'}
          />
          <span className="avatars">
            {presents.map((x) => (
              <span
                key={x.id}
                className="avatar"
                title={x.id === moi?.id ? `${x.nom} (vous)` : x.nom}
                style={{ background: couleurPersonne(x.id), color: '#fff' }}
              >
                {initiales(x.nom).slice(0, 2)}
              </span>
            ))}
          </span>
        </div>
      </div>
      <div className="entete-projet-meta">
        <label className="filtre-pour">
          <span className="discret">Statut</span>
          <select
            value={p.statut}
            disabled={!p.ecriture}
            onChange={(e) => void modifier({ statut: e.target.value }).catch(() => undefined)}
            aria-label="Statut du projet"
          >
            {STATUTS_PROJET.map((s) => (
              <option key={s} value={s}>
                {LIBELLES_STATUT_PROJET[s]}
              </option>
            ))}
          </select>
        </label>
        <EnLigne
          libelle="les dates"
          champs={[
            { nom: 'dateDebut', libelle: 'Début', type: 'date' },
            { nom: 'dateFin', libelle: 'Fin', type: 'date' },
          ]}
          initial={p}
          modifiable={p.ecriture}
          onEnregistrer={modifier}
        >
          <span className={p.depasse ? 'alerte' : 'discret'}>
            <Icone nom="date_range" /> {periode ?? 'Sans dates'}
            {p.depasse && ' (dépassé)'}
          </span>
        </EnLigne>
        {moi && p.ecriture && (
          <EnLigne
            libelle="l’entité"
            champs={[
              {
                nom: 'entiteId',
                libelle: 'Entité',
                type: 'choix',
                requis: !moi.administrateur,
                options: entites
                  .filter((e) => !e.archivee && ecrit(moi, e.id))
                  .map((e) => ({ valeur: e.id, libelle: e.nom })),
                aide: 'Dans le même espace seulement : ses tâches y restent.',
              },
            ]}
            initial={p}
            modifiable
            onEnregistrer={modifier}
          >
            <span className="discret">
              <Icone nom="domain" /> {p.entite ?? 'Sans entité'}
            </span>
          </EnLigne>
        )}
        <span className="avancement" aria-label={`${faites} tâche(s) faite(s) sur ${total}`}>
          <span className="avancement-barre">
            <span style={{ width: total ? `${Math.round((faites / total) * 100)}%` : 0 }} />
          </span>
          <span className="mono">
            {faites}/{total} faites
          </span>
        </span>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
    </header>
  )
}

/**
 * Les pages en onglets : l'objectif, puis les sous-pages dans l'ordre de l'arbre (une sous-page
 * de sous-page dit sa mère : « A › A1 ») ; en ajouter une sous la page ouverte.
 */
function OngletsPages({
  p,
  page,
  onOuvrir,
}: {
  p: FicheProjet
  page: FicheProjet['pages'][number]
  onOuvrir: (pageId: string) => void
}) {
  const client = useQueryClient()
  const [ajout, setAjout] = useState(false)
  const titres = new Map(p.pages.map((x) => [x.id, x.titre]))
  const libelle = (x: FicheProjet['pages'][number]) =>
    x.profondeur > 1 && x.parentId ? `${titres.get(x.parentId)} › ${x.titre}` : x.titre
  return (
    <div className="onglets-pages">
      <div className="onglets-pages-rangee" role="tablist" aria-label="Pages du projet">
        {p.pages.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={x.id === page.id}
            className={x.id === page.id ? 'onglet-page actif' : 'onglet-page'}
            onClick={() => onOuvrir(x.id)}
          >
            {x.parentId === null && <Icone nom="rocket_launch" />}
            {libelle(x)}
          </button>
        ))}
        {p.ecriture && !ajout && (
          <button
            type="button"
            className="onglet-page ajout"
            onClick={() => setAjout(true)}
            title={`Nouvelle sous-page de « ${page.titre} »`}
          >
            <Icone nom="note_add" />
            Sous-page
          </button>
        )}
      </div>
      {ajout && (
        <Formulaire
          enLigne
          champs={[
            {
              nom: 'titre',
              libelle: `Sous-page de « ${page.titre} »`,
              type: 'texte',
              requis: true,
            },
          ]}
          libelleBouton="Créer"
          onAnnuler={() => setAjout(false)}
          onValider={async (v) => {
            const nouvelle = await lire(
              api.projets[':id'].pages.$post({
                param: { id: p.id },
                json: { titre: String(v.titre), parentId: page.id },
              }),
            )
            await client.invalidateQueries({ queryKey: ['projets', 'fiche', p.id] })
            setAjout(false)
            onOuvrir(nouvelle.id)
          }}
        />
      )}
    </div>
  )
}

/** Titre de la page ouverte : renommer, déplacer, changer de place, supprimer. */
function TitrePage({
  p,
  page,
  onOuvrir,
}: {
  p: FicheProjet
  page: FicheProjet['pages'][number]
  onOuvrir: (pageId: string) => void
}) {
  const client = useQueryClient()
  const [erreur, setErreur] = useState<string | null>(null)
  const relire = () => client.invalidateQueries({ queryKey: ['projets', 'fiche', p.id] })
  const agir = async (f: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await f()
      await relire()
    } catch (e) {
      setErreur((e as Error).message)
    }
  }
  const modifier = (json: { titre?: string; parentId?: string; ordre?: number }) =>
    lire(api.projets.pages[':id'].$patch({ param: { id: page.id }, json }))
  const soeurs = p.pages.filter((x) => x.parentId === page.parentId)
  const rang = soeurs.findIndex((x) => x.id === page.id)
  // Échanger sa place avec une sœur : chacune prend l'ordre de l'autre (rang si égaux).
  const echanger = (autre: (typeof soeurs)[number] | undefined) =>
    autre &&
    agir(async () => {
      const a = page.ordre === autre.ordre ? rang : page.ordre
      const b = page.ordre === autre.ordre ? soeurs.indexOf(autre) : autre.ordre
      await modifier({ ordre: b })
      await lire(api.projets.pages[':id'].$patch({ param: { id: autre.id }, json: { ordre: a } }))
    })
  const meres = p.pages.filter((x) => x.id !== page.id && !creeUnCycle(p.pages, page.id, x.id))
  return (
    <div className="titre-page">
      <h2>
        <EnLigne
          libelle="le titre de la page"
          champs={[{ nom: 'titre', libelle: 'Titre', type: 'texte', requis: true }]}
          initial={page}
          modifiable={p.ecriture}
          onEnregistrer={(v) => agir(() => modifier({ titre: String(v.titre) }))}
        >
          {page.titre}
        </EnLigne>
      </h2>
      {page.contenuModifiePar && (
        <span className="discret">Modifiée en dernier par {page.contenuModifiePar}</span>
      )}
      {p.ecriture && page.parentId !== null && (
        <div className="ligne-actions">
          <button
            type="button"
            className="bouton-icone"
            aria-label="Monter"
            title="Monter"
            disabled={rang <= 0}
            onClick={() => echanger(soeurs[rang - 1])}
          >
            <Icone nom="keyboard_arrow_up" />
          </button>
          <button
            type="button"
            className="bouton-icone"
            aria-label="Descendre"
            title="Descendre"
            disabled={rang >= soeurs.length - 1}
            onClick={() => echanger(soeurs[rang + 1])}
          >
            <Icone nom="keyboard_arrow_down" />
          </button>
          <label className="filtre-pour">
            <span className="discret">Sous</span>
            <select
              value={page.parentId}
              onChange={(e) => void agir(() => modifier({ parentId: e.target.value }))}
              aria-label="Déplacer sous la page"
            >
              {meres.map((x) => (
                <option key={x.id} value={x.id}>
                  {'· '.repeat(x.profondeur)}
                  {x.titre}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="bouton-icone"
            aria-label="Supprimer la page"
            title="Supprimer la page (et ses sous-pages)"
            onClick={async () => {
              if (!(await confirmer(`Supprimer « ${page.titre} » et ses sous-pages ?`))) return
              await agir(async () => {
                await lire(api.projets.pages[':id'].$delete({ param: { id: page.id } }))
                // Retour à sa mère (la page supprimée n'est plus rendue).
                onOuvrir(page.parentId!)
              })
            }}
          >
            <Icone nom="delete" />
          </button>
        </div>
      )}
      {erreur && <p className="alerte">{erreur}</p>}
    </div>
  )
}
